import { gsap } from 'gsap'

export default class Intro {
  constructor({ skip = false } = {}) {
    this.bindMethods()
    this.getElems()

    if (!this.el) return

    this.portion = 1 / (this.medias.length + 1) * 100
    this.progress = 0
    this.lastSetProgress = this.progress
    this.bumpPromises = []
    this.currentBump = null
    this.skip = skip
    if (wp_vars.isDev == 1) this.skip = true

    if (skip) this.el.style.display = 'none'

    else {
      this.prepareLoad()
      this.watchLoadImages()
      this.bumpProgress(this.portion * 0.5)
    }
  }

  bindMethods() {
    this.loaded = this.loaded.bind(this)
    this.hide = this.hide.bind(this)
  }

  getElems() {
    this.el = document.querySelector('.js-intro')
    if (this.el) this.medias = this.el.querySelectorAll('.js-intro-media')
  }

  /**
   * Prepares the loading of media by creating a Promise for each picture el.
   *
   * @returns {void}
   */
  prepareLoad() {
    for (const media of this.medias) {
      media.loadedPromise = new Promise((resolve) => {
        media.resolvePromise = resolve
      })

      media.$onLoaded = () => {
        const img = media.querySelector('img')
        const loadedMedia = {
          media,
          img,
          src: img.currentSrc
        }

        this.bumpProgress()

        media.parentElement.classList.add('loaded')
        media.resolvePromise(loadedMedia)
      }
    }
  }

  /**
   * Starts the event listener to wait for images to be loaded.
   *
   * @returns {void}
   */
  watchLoadImages() {
    // @TODO: ADAPT TO NATIVE LAZYLOADING
    // document.addEventListener('lazyloaded', this.loaded)
  }

  /**
   * Bumps the progress to a new value.
   *
   * @param {Number} progress The new progress value (<= 100).
   *
   * @returns {Promise} A Promise resolved when the animation to the new value has ended.
   */
  bumpProgress(progress = Math.min(this.lastSetProgress + this.portion, 100)) {
    if (!this.el) return Promise.resolve()

    const p = new Promise((resolve) => {
      const tl = gsap.timeline({
        paused: !!this.currentBump,
        onStart: () => {
          this.currentBump = p
        },
        onComplete: () => {
          this.currentBump = null

          resolve()
        }
      })
      const value = { progress: this.lastSetProgress }

      if (this.currentBump) {
        Promise.all(this.bumpPromises)
          .then(() => {
            tl.play()
          })
      }

      this.lastSetProgress = progress

      tl.add('start')
        .to(value, {
          duration: 1,
          ease: 'power2.inOut',
          progress,
          onUpdate: () => {
            if (this.progressEl) {
              const progressString = Math.round(value.progress).toString().padStart(3, '0')

              this.progressEl.textContent = progressString
            }
          }
        })
    })

    this.bumpPromises.push(p)

    return p
  }

  /**
   * Called when an image has been loaded.
   * Instantly resolves the image's Promise.
   *
   * @param {Event} event The event object.
   *
   * @returns {void}
   */
  loaded({ target }) {
    if (target.parentElement.classList.contains('js-intro-media') && target.parentElement.$onLoaded) target.parentElement.$onLoaded()
  }

  /**
   * Waits for all images to be loaded.
   * Then call .hide().
   * Then call .destroy().
   * Called by App.js.
   *
   * @returns {Promise} A Promise resolved when ALL images has been loaded, the div is hidden, and destroyed. OR it is resolved instantly if this.skip === true.
   */
  load() {
    if (!this.el || this.skip) return Promise.resolve()

    const promises = []

    for (const media of this.medias) {
      promises.push(media.loadedPromise)
    }

    return Promise.all(promises)
      .then(() => {
        // @TODO: ADAPT TO NATIVE LAZYLOADING
        // document.removeEventListener('lazyloaded', this.loaded)

        return this.bumpProgress(100)
      })
      .then(this.hide)
      .then(() => this.destroy())
  }

  /**
   * Hides the Intro.
   * Called when ALL images have been loaded.
   *
   * @returns {Promise} A Promise resolved when the hide animation has ended.
   */
  hide() {
    return new Promise((resolve) => {
      if (!this.el) return resolve()

      const tl = gsap.timeline({
        delay: 0.25,
        onComplete: resolve
      })

      tl.to(this.el, {
        duration: 1.5,
        alpha: 0,
        display: 'none',
        ease: 'power2.inOut'
      })
    })
  }

  /**
   * Destroys the Intro.
   * Called when the Intro has been hidden.
   *
   * @returns {void}
   */
  destroy() {}
}
